// KleonCredits.tsx — compteur « Crédits Kléon » en haut à droite de l'en-tête (2026-10-29)
//
// Affichage seul : 1 crédit par tranche de 10 minutes de direct terminée, plus 1 crédit par
// like reçu sur les vidéos du créateur (admin-api CreatorAccountController::credits, calculé
// à la demande). Relu toutes les minutes, au retour sur l'onglet et au clic ; une relecture
// qui échoue garde le dernier solde affiché plutôt que de le faire disparaître.

import React, { useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import styles from './Header.module.scss';
import { CreatorCredits, fetchCreatorCredits } from '../../../backendServices/adminManagment/api_creatorAccount';

const REFRESH_MS = 60_000;

const formatHours = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min`;
};

const KleonCredits: React.FC<{ adminId: number | null }> = ({ adminId }) => {
  const [credits, setCredits] = useState<CreatorCredits | null>(null);

  const refresh = useCallback(() => {
    if (adminId === null || document.hidden) return;
    fetchCreatorCredits(adminId).then(setCredits).catch(() => { /* on garde le dernier solde */ });
  }, [adminId]);

  useEffect(() => {
    setCredits(null);
    refresh();
    const timer = window.setInterval(refresh, REFRESH_MS);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [adminId, refresh]);

  if (credits === null) return null;

  const blockMinutes = Math.round(credits.rates.blockSeconds / 60);
  const title =
    `Crédits Kléon : ${credits.credits}\n`
    + `• Direct : ${credits.broadcastCredits} (${formatHours(credits.broadcastSeconds)} diffusées, ${credits.rates.creditsPerBlock} crédits par tranche de ${blockMinutes} min terminée)\n`
    + `• Likes : ${credits.likeCredits} (${credits.likes} like${credits.likes > 1 ? 's' : ''}, ${credits.rates.creditsPerLike} crédits par like)\n`
    + `• Vidéos et directs regardés : ${credits.viewingCredits ?? 0} (1 crédit par 10 minutes)`;

  return (
    <div className={classNames(styles.quotaInfo)} title={title} onClick={refresh} style={{ cursor: 'default' }}>
      <span aria-hidden className="text-amber-300">✦ </span>Crédits Kléon : <b>{credits.credits.toLocaleString('fr-FR')}</b>
    </div>
  );
};

export default KleonCredits;
